"use client";

import { Box, colors } from "@mui/material";
import MediaV2 from "@v2/components/MediaV2";
import { PLPFilterProductClass, PLPSearchParams } from "../../products/(plp)/types";
import { useBrandCategoryNavigation } from "./brandCategoryNavigation";

export default function BrandCategoryCards({
    categories,
    brandSlug,
    searchParams,
}: {
    categories: PLPFilterProductClass[];
    brandSlug: string;
    searchParams: PLPSearchParams;
}) {
    const { isActive, go, onKeyDown } = useBrandCategoryNavigation(brandSlug, searchParams);

    return (
        <Box sx={{
            gridArea: "aside",
            // Below the split layout there is no column to fill, so the cards behave like
            // the ones on the products page: a row of fixed squares that scrolls sideways
            // and reaches the screen edge. From lg they go back to filling the hero slot.
            "--brand-category-size": { xs: "148px", sm: "234px" },
            display: { xs: "flex", lg: "grid" },
            gridTemplateColumns: { lg: `repeat(${categoryColumns(categories.length)}, 1fr)` },
            gap: { xs: 2, sm: 3, lg: 2.5 },
            alignContent: { lg: "center" },
            overflowX: { xs: "auto", lg: "visible" },
            overscrollBehaviorX: "contain",
            scrollSnapType: { xs: "x mandatory", lg: "none" },
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            mx: { xs: -2, lg: 0 },
            px: { xs: 2, lg: 0 },
        }}>
            {categories.map((category) => {
                const active = isActive(category.slug);

                return (
                    <Box
                        key={category.id}
                        role="button"
                        tabIndex={0}
                        aria-pressed={active}
                        onClick={() => go(category)}
                        onKeyDown={onKeyDown(category)}
                        sx={{
                            position: "relative",
                            display: "block",
                            flexShrink: 0,
                            width: { xs: "var(--brand-category-size)", lg: "auto" },
                            scrollSnapAlign: { xs: "center", lg: "none" },
                            aspectRatio: "1 / 1",
                            bgcolor: "#fff",
                            borderRadius: { xs: 1.2, md: 2 },
                            overflow: "hidden",
                            cursor: "pointer",
                            userSelect: "none",
                            "&:hover .brand_category_image": { filter: active ? "grayscale(0.1)" : "grayscale(0.4)" },
                            "&:hover .brand_category_title": { color: active ? "#fff" : colors.grey[900] },
                        }}
                        onDragStart={(event) => event.preventDefault()}
                    >
                        <MediaV2
                            media_id={category.media_id}
                            alt={category.site_name}
                            height={234}
                            options={{ quality: 80 }}
                            imageProps={{ draggable: false, fill: true, style: { objectFit: "cover" } }}
                            boxProps={{
                                className: "brand_category_image",
                                sx: {
                                    width: "100%",
                                    aspectRatio: "1 / 0.8",
                                    filter: active ? "grayscale(0.1)" : "grayscale(1)",
                                    transition: "120ms",
                                },
                            }}
                        />

                        <Box
                            className="brand_category_title"
                            sx={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                left: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                aspectRatio: "1 / 0.2",
                                bgcolor: active ? colors.grey[900] : colors.grey[300],
                                color: active ? "#fff" : colors.grey[700],
                                fontSize: { xs: 12, md: 16 },
                                fontWeight: 500,
                                transition: "200ms",
                            }}
                        >
                            {category.site_name}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

// The hero never renders fewer than two cards, so the grid is two columns except
// when there are exactly three to lay out.
function categoryColumns(count: number) {
    return count === 3 ? 3 : 2;
}
